import { __assign, __extends, __read, __spreadArray } from "tslib";
import { BatchClient } from '@slardar/sdk-core';
import { mergeClientConfig, toBackendOptions } from './config';
import { BackendEventType, BreadcrumbType, ReportEventType, SendEventType, BreadcrumbCategory, } from './types';
import { isHitBySampleRate } from './utils';
import { log } from './utils/logger';
/**
 * The Mini Program Client
 */
var MiniProgramClient = /** @class */ (function (_super) {
    __extends(MiniProgramClient, _super);
    function MiniProgramClient(config, Backend) {
        var _this = _super.call(this, config) || this;
        _this.deviceId = '';
        _this.envInfo = {};
        _this.appSessionId = '';
        _this.pageSessionId = '';
        _this.preQueue = [];
        _this.ready = false; // lock the client
        _this.config = config;
        _this.pid = config.pid;
        var backendOptions = toBackendOptions(_this.config);
        _this.backend = new Backend(backendOptions, _this.reportBackendEvent.bind(_this));
        _this.backend.init();
        _this.breadcrumbs = [];
        _this.sampleHit = isHitBySampleRate(_this.config.sampleRate);
        _this.backend.collectEnvInfo();
        if (_this.config.finderInstance) {
            _this.backend.collectFinderConfig();
            _this.backend.collectTesterSdkVersion();
        }
        return _this;
    }
    MiniProgramClient.prototype.updateConfig = function (config) {
        var _a;
        this.config = mergeClientConfig(this.config, config);
        if ((_a = this.finderConfig) === null || _a === void 0 ? void 0 : _a.user_unique_id) {
            this.config.userId = this.finderConfig.user_unique_id;
        }
    };
    MiniProgramClient.prototype.reportError = function (error, context) {
        var _a;
        this.reportEvent({
            ev_type: ReportEventType.JS_ERROR,
            event: {
                name: error.name,
                message: error.message,
                stack: (_a = error.stack) !== null && _a !== void 0 ? _a : '',
            },
            breadcrumbs: __spreadArray([], __read(this.breadcrumbs), false),
        }, context);
    };
    MiniProgramClient.prototype.reportEvent = function (event, context) {
        if (!this.ready) {
            // 状态没有 ready 的时候，先存入队列
            this.preQueue.push(__assign(__assign({}, event), { context: context }));
        }
        else {
            _super.prototype.sendEvent.call(this, __assign(__assign({}, event), { context: context }));
        }
    };
    MiniProgramClient.prototype.addBreadcrumb = function (type, payload) {
        var mb = this.config.maxBreadcrumbs;
        var newBreadcrumb = __assign(__assign({}, payload), { type: type, timestamp: Date.now() });
        this.breadcrumbs = mb >= 0 ? __spreadArray(__spreadArray([], __read(this.breadcrumbs), false), [newBreadcrumb], false).slice(-mb) : __spreadArray(__spreadArray([], __read(this.breadcrumbs), false), [newBreadcrumb], false);
    };
    MiniProgramClient.prototype.flush = function () {
        this._uploadQueue();
    };
    MiniProgramClient.prototype.sendPageView = function () {
        log('pageView', this.pid, this.pageSessionId);
        this.reportEvent({
            ev_type: ReportEventType.PAGEVIEW,
        });
    };
    /**
     * Report an event from backend, will be passed to the backend as callback
     * @param event
     */
    MiniProgramClient.prototype.reportBackendEvent = function (event) {
        switch (event.ev_type) {
            case BackendEventType.ENV_INFO: {
                this.envInfo = event.data;
                this.unlock();
                break;
            }
            case BackendEventType.DEVICE_ID: {
                this.deviceId = event.data.deviceId;
                break;
            }
            case BackendEventType.FINDER_CONFIG: {
                this.finderConfig = event.data;
                break;
            }
            case BackendEventType.TESTER_SDK_VERSION: {
                this.testerAbSdkVersion = event.data.ab_sdk_version;
                break;
            }
            case BackendEventType.NETWORK_STATUS: {
                if (this.envInfo) {
                    this.envInfo = __assign(__assign({}, this.envInfo), { network_type: event.data.network_type });
                }
                break;
            }
            case BackendEventType.GLOBAL_JS_ERROR: {
                this.reportEvent({
                    ev_type: ReportEventType.JS_ERROR,
                    event: event.data,
                    breadcrumbs: __spreadArray([], __read(this.breadcrumbs), false),
                });
                break;
            }
            case BackendEventType.JS_ERROR: {
                this.reportEvent({
                    ev_type: ReportEventType.JS_ERROR,
                    event: event.data,
                    breadcrumbs: __spreadArray([], __read(this.breadcrumbs), false),
                }, event.context);
                break;
            }
            case BackendEventType.APP_SESSION: {
                this.appSessionId = event.data.appSessionId;
                break;
            }
            case BackendEventType.PAGE_SESSION: {
                if (this.pageSessionId !== event.data.pageSessionId) {
                    this.addBreadcrumb(BreadcrumbType.DOM, {
                        category: BreadcrumbCategory.ROUTE,
                        message: event.data.pid,
                    });
                    this.pid = event.data.pid;
                    this.pageSessionId = event.data.pageSessionId;
                    this.sendPageView();
                }
                break;
            }
            case BackendEventType.REQUEST: {
                this.reportEvent({
                    ev_type: ReportEventType.REQUEST,
                    event: event.data,
                }, event.context);
                break;
            }
            case BackendEventType.SET_DATA: {
                this.reportEvent({
                    ev_type: ReportEventType.SET_DATA,
                    event: event.data,
                }, event.context);
                break;
            }
            case BackendEventType.PAGE_ON_READY: {
                this.reportEvent({
                    ev_type: ReportEventType.ON_READY,
                    event: event.data,
                });
                break;
            }
            case BackendEventType.PAGE_FIRST_LOAD: {
                this.reportEvent({
                    ev_type: ReportEventType.FIRST_LOAD,
                    event: event.data,
                }, event.context);
                break;
            }
        }
    };
    MiniProgramClient.prototype._shouldSend = function () {
        return this.sampleHit;
    };
    /**
     * @inheritdoc
     */
    MiniProgramClient.prototype.buildParams = function (options) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return __assign(__assign({}, options), { common: __assign(__assign({}, this.envInfo), { aid: this.config.aid, pid: (_b = (_a = options.context) === null || _a === void 0 ? void 0 : _a.pid) !== null && _b !== void 0 ? _b : this.pid, rangers_sdk_version: this.config.version, timestamp: Date.now(), user_unique_id: this.finderConfig ? this.finderConfig.user_unique_id : this.config.userId || this.deviceId, device_id: this.deviceId, session_id: this.config.sessionId, app_session_id: this.appSessionId, page_session_id: (_d = (_c = options.context) === null || _c === void 0 ? void 0 : _c.pageSessionId) !== null && _d !== void 0 ? _d : this.pageSessionId, context: (_f = (_e = options.context) === null || _e === void 0 ? void 0 : _e.context) !== null && _f !== void 0 ? _f : this.config.context, uuid: (_g = this.finderConfig) === null || _g === void 0 ? void 0 : _g.user_unique_id, ssid: (_h = this.finderConfig) === null || _h === void 0 ? void 0 : _h.ssid, ab_sdk_version: this.testerAbSdkVersion }), context: undefined });
    };
    /**
     * @inheritdoc
     */
    MiniProgramClient.prototype._request = function (options) {
        if (!options || !options.event)
            return;
        var type = options.type, event = options.event;
        if (type === 'post') {
            this.backend.sendEvent({
                ev_type: SendEventType.BATCH,
                list: event.list,
            });
        }
    };
    MiniProgramClient.prototype.unlock = function () {
        var _this = this;
        this.ready = true;
        this.preQueue.forEach(function (event) {
            _super.prototype.sendEvent.call(_this, event);
        });
        this.preQueue = [];
    };
    return MiniProgramClient;
}(BatchClient));
export default MiniProgramClient;
//# sourceMappingURL=client.js.map